import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  useDevices,
    useApproveDevice,
  useRejectDevice,
  useUnassignDeviceFromAgent,
} from "@/hooks/use-devices";
import {
  IconRefresh,
  IconSearch,
  IconDeviceMobile,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconDots,
  IconAlertCircle,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import type { DeviceParams } from "@/types/api";
import { formatDistanceToNow } from "date-fns";

import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/devices/")({
  component: DevicesPage,
});
function DevicesPage() {
  return (
    <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
      <AppShell>
        <DevicesPageContent />
      </AppShell>
    </ProtectedRoute>
  );
}


function DevicesPageContent() {
  const [filters, setFilters] = useState<DeviceParams>({
    page: 1,
    limit: 50,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [rejectDialog, setRejectDialog] = useState<{
    open: boolean;
    deviceId: string;
    deviceName: string;
  }>({ open: false, deviceId: "", deviceName: "" });

  const { data, isLoading, refetch } = useDevices(filters);

  // Calculate stats from device data
  const stats = data?.data ? {
    total: data.data.length,
    online: data.data.filter(d => d.status === 'online').length,
    offline: data.data.filter(d => d.status === 'offline').length,
    disabled: data.data.filter(d => d.status === 'disabled').length,
    pending: data.data.filter(d => d.status === 'pending').length,
  } : null;
  const approveDevice = useApproveDevice();
  const rejectDevice = useRejectDevice();
  const unassignDevice = useUnassignDeviceFromAgent();

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // Client-side search
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value);
    setFilters((prev) => ({
      ...prev,
      status: value === "all" ? undefined : (value as any),
      page: 1,
    }));
  };

  const handleApprove = async (deviceId: string, deviceName: string) => {
    try {
      await approveDevice.mutateAsync({
        deviceId,
        data: {},
      });
      toast.success(`Device ${deviceName} approved successfully`);
    } catch (error: any) {
      toast.error(error.message || "Failed to approve device");
    }
  };

  const handleReject = async () => {
    try {
      await rejectDevice.mutateAsync({
        deviceId: rejectDialog.deviceId,
        data: {
          reason: "Rejected via admin dashboard",
        },
      });
      toast.success(`Device ${rejectDialog.deviceName} rejected successfully`);
      setRejectDialog({ open: false, deviceId: "", deviceName: "" });
    } catch (error: any) {
      toast.error(error.message || "Failed to reject device");
    }
  };

  const handleUnassign = async (deviceId: string, deviceName: string) => {
    try {
      await unassignDevice.mutateAsync(deviceId);
      toast.success(`Device ${deviceName} unassigned successfully`);
    } catch (error: any) {
      toast.error(error.message || "Failed to unassign device");
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "online":
        return "default";
      case "offline":
        return "secondary";
      case "disabled":
        return "destructive";
      case "pending":
        return "outline";
      default:
        return "outline";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "online":
        return <IconCircleCheck className="h-3 w-3 text-green-500" />;
      case "offline":
        return <IconCircleX className="h-3 w-3 text-gray-500" />;
      case "disabled":
        return <IconAlertCircle className="h-3 w-3 text-red-500" />;
      case "pending":
        return <IconClock className="h-3 w-3 text-orange-500" />;
      default:
        return null;
    }
  };

  // Client-side filtering for search
  const filteredData = data?.data.filter((device) =>
    searchTerm
      ? (device as any).device_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (device as any).device_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (device.agent as any)?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      : true,
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Device Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage devices and approvals
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by device name, ID, manufacturer, model, or agent..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={handleStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <IconRefresh className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Total Devices
              </p>
              <IconDeviceMobile className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">{stats.total}</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Online
              </p>
              <IconCircleCheck className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{stats.online}</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Offline
              </p>
              <IconCircleX className="h-4 w-4 text-gray-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{stats.offline}</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Disabled
              </p>
              <IconAlertCircle className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{stats.disabled}</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Pending Approval
              </p>
              <IconClock className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold mt-2">{stats.pending}</p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Device</TableHead>
              <TableHead>Hardware</TableHead>
              <TableHead>Software</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>Last Heartbeat</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  Loading devices...
                </TableCell>
              </TableRow>
            ) : !filteredData || filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  No devices found
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((device) => (
                <TableRow key={device.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{(device as any).device_name}</div>
                      <div className="text-sm text-muted-foreground font-mono">
                        {(device as any).device_id}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>
                        {(device as any).device_name}
                      </div>
                      <div className="text-muted-foreground">
                        {(device as any).android_version || (device as any).app_version ? `Android ${(device as any).android_version || 'N/A'}` : 'iOS'}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{(device as any).app_version ? `v${(device as any).app_version}` : 'N/A'}</Badge>
                  </TableCell>
                  <TableCell>
                    {device.agent ? (
                      <div className="text-sm">{(device as any).agent?.name} ({(device as any).agent?.serial_number})</div>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        Unassigned
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(device as any).last_heartbeat
                      ? formatDistanceToNow(new Date((device as any).last_heartbeat), {
                          addSuffix: true,
                        })
                      : "Never"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(device.status)}
                      <Badge variant={getStatusBadgeVariant(device.status)}>
                        {device.status}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    {(device as any).created_at
                      ? formatDistanceToNow(new Date((device as any).created_at), {
                          addSuffix: true,
                        })
                      : "Unknown"}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <IconDots className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>View Details</DropdownMenuItem>
                        {device.status === "pending" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() =>
                                handleApprove(device.id, (device as any).device_name)
                              }
                            >
                              Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() =>
                                setRejectDialog({
                                  open: true,
                                  deviceId: device.id,
                                  deviceName: (device as any).device_name,
                                })
                              }
                            >
                              Reject
                            </DropdownMenuItem>
                          </>
                        )}
                        {device.agent && device.status !== "pending" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() =>
                                handleUnassign(device.id, (device as any).device_name)
                              }
                            >
                              Unassign from Agent
                            </DropdownMenuItem>
                          </>
                        )}
                        {!device.agent && device.status !== "pending" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                              Assign to Agent
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {((data as any)?.count || 0) > filters.limit! && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(filters.page! - 1) * filters.limit! + 1} to{" "}
            {Math.min(
              filters.page! * filters.limit!,
              data?.data?.length || 0,
            )}{" "}
            of {(data as any)?.count || data?.data?.length || 0} devices
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page === 1}
              onClick={() =>
                setFilters((prev) => ({ ...prev, page: prev.page! - 1 }))
              }
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page! * filters.limit! >= ((data as any)?.count || 0)}
              onClick={() =>
                setFilters((prev) => ({ ...prev, page: prev.page! + 1 }))
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Reject Confirmation Dialog */}
      <AlertDialog
        open={rejectDialog.open}
        onOpenChange={(open) =>
          !open &&
          setRejectDialog({ open: false, deviceId: "", deviceName: "" })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Device?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reject device{" "}
              <span className="font-semibold">{rejectDialog.deviceName}</span>?
              This device will not be able to process transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
